import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import { TUpdateStockOutStatusReduxParams } from '~types/feat/approval';
import { MetalTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';
import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import * as Styles from './_stockOutApprovalTable.module.scss';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

const StockOutApprovalTable = () => {
    const dispatch = useAppDispatch();

    const { Misc: { dateTimeFormat } } = UtilFunctions;

    const stockOutListing = useAppSelector(state => state.approval.pendingStockOutListing.data)
        .filter(item => item.status === TransactionStatusEnum.Pending);
    const isLoading = useAppSelector(state => state.approval.loadings.getPendingStockOutListing);
    const access = useAppSelector(state => state.auth.userDetails.access);
    const filters = useAppSelector(state => state.approval.pendingStockOutListing.filters);
    const allProducts = useAppSelector(state => state.gold.allProducts);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateStockOutState = (data: TUpdateStockOutStatusReduxParams) =>
        dispatch(combinedActions.approvalSetTempUpdateStockOutState(data));

    const setConfirmModalIsOpen = (open: boolean) =>
        dispatch(combinedActions.approvalSetConfirmUpdateStockOutStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateStockOutStatusReduxParams) => {
        setConfirmModalIsOpen(true);
        setTempUpdateStockOutState(data);
    };

    const handleRejectClick = (data: TUpdateStockOutStatusReduxParams) => {
        setConfirmModalIsOpen(true);
        setTempUpdateStockOutState(data);
    };

    const tableHeader = [
        'PRODUCT NAME',
        'SERIAL NO.',
        'BRANCH CODE',
        'BRANCH NAME',
        'AMOUNT',
        'DATE',
        'ACTION',
    ];

    const renderActionButton = (id: number, status: number) => {
        if (status === 1) {
            return (
                <div
                    key={id}
                    className={Styles['action-button-container']}
                >
                    {hasEditAccess
                        && (
                            <ActionButton
                                label='Approve'
                                onClick={() => handleApproveClick({ id, status: TransactionStatusEnum.Approved })}
                                className={`${Styles['action-button']} ${Styles.approve}`}
                            />
                        )
                    }

                    {hasEditAccess
                        && (
                            <ActionButton
                                label='Reject'
                                onClick={() => handleRejectClick({ id, status: TransactionStatusEnum.Rejected })}
                                className={`${Styles['action-button']} ${Styles.reject}`}
                            />
                        )
                    }
                </div>
            );
        }
        return '-';
    };

    const tableBody = stockOutListing.map(item => {
        const {
            id,
            productDetails: {
                productId,
                productName,
            },
            branchDetails: {
                branchCode,
                branchName,
            },
            serialNumbers,
            requestedAmount,
            status,
            createdAt,
        } = item;

        const product = allProducts.find(
            product => String(product.productId) === String(productId),
        );

        const isGold = product?.metalType === MetalTypeEnum.Gold;

        const handleViewSerialNo = () => {
            setIsSerialNoModalOpen(true);
            setSerialNoList(serialNumbers || []);
        };

        return {
            className: isGold ? Styles['gold-row'] : '',

            cells: [
                productName,
                serialNumbers?.length > 0
                    ? (
                        <ActionButton
                            key={`serial-${id}`}
                            label='View'
                            onClick={handleViewSerialNo}
                        />
                    )
                    : '-',
                branchCode,
                branchName,
                requestedAmount,
                dateTimeFormat(createdAt),
                renderActionButton(Number(id), status),
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={isLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default StockOutApprovalTable;
